import { EntranceGroup } from '../app/Components/attendance/entrance-groups/entrance-groups.component';

/**
 * Pure, framework-free derivations of the attendance "modern view" metrics. Both
 * the modern-summary component and the big-screen dashboard widgets read from here
 * so they render identical figures from the same `attendanceSummary` shape.
 *
 * None of these functions mutate their inputs.
 */

/** One entrance's headline figures, or a summed group tile named after the group. */
export interface EntranceRate {
  name: string;
  entries: number;
  scanRate: number;
  inVenue: number;
}

/** Per-event contribution to an error result code. */
export interface ErrorEventDetail {
  eventCode: string;
  name: string;
  rate: number;
  total: number;
}

/** A result code rolled up across every event that produced it. */
export interface ErrorAggregate {
  code: string;
  rate: number;
  total: number;
  percentOfEntries: number;
  events: string[];
  eventDetails: ErrorEventDetail[];
}

/** The three venue-wide scan-rate totals shown on the hero. */
export interface ScanRateTotals {
  totalSuccessRate: number;
  totalErrorRate: number;
  totalScanRate: number;
}

/** localStorage key prefixes, shared so every consumer reads the same entrance groups. */
const ENTRANCE_GROUPS_KEY = 'attendanceEntranceGroups';
const GROUPING_ON_KEY = 'attendanceGroupingOn';

export function entranceGroupsStorageKey(venueKey: string): string {
  return `${ENTRANCE_GROUPS_KEY}:${venueKey}`;
}

export function groupingOnStorageKey(venueKey: string): string {
  return `${GROUPING_ON_KEY}:${venueKey}`;
}

/**
 * Every entrance name present on the summary, in the summary's own order. Unlike
 * {@link computeEntranceRates} this is unfiltered, so a zero-activity entrance is
 * still assignable to a group.
 */
export function entranceNames(summary: any): string[] {
  return Object.keys(summary?.entrances || {});
}

/**
 * Active entrances as rate tiles: those with any scan rate or entries, busiest
 * first (by scan rate, then entries).
 */
export function computeEntranceRates(summary: any): EntranceRate[] {
  const entrances = summary?.entrances || {};
  return Object.keys(entrances)
    .map((name) => ({
      name,
      entries: Number(entrances[name]?.entries || 0),
      scanRate: Number(entrances[name]?.scanRate || 0),
      inVenue: Number(entrances[name]?.inVenue || 0),
    }))
    .filter((e) => e.scanRate > 0 || e.entries > 0)
    .sort((a, b) => b.scanRate - a.scanRate || b.entries - a.entries);
}

/**
 * Applies entrance groups to a rate list. With grouping off or no groups, the list
 * is returned unchanged. Otherwise each group's members are summed into one tile
 * named after the group, entrances in no group stay individual, and the same
 * "has activity" filter and busiest-first sort are re-applied.
 */
export function applyEntranceGroups(
  rates: EntranceRate[],
  groups: EntranceGroup[],
  groupingOn: boolean
): EntranceRate[] {
  if (!groupingOn || !groups || groups.length === 0) {
    return rates;
  }

  const byName = new Map(rates.map((e) => [e.name, e]));
  const grouped = new Set<string>();
  const tiles: EntranceRate[] = [];

  for (const group of groups) {
    const summed: EntranceRate = { name: group.name, entries: 0, scanRate: 0, inVenue: 0 };
    for (const name of group.entranceNames) {
      grouped.add(name);
      const ent = byName.get(name);
      if (!ent) continue;
      summed.entries += ent.entries;
      summed.scanRate += ent.scanRate;
      summed.inVenue += ent.inVenue;
    }
    summed.scanRate = Math.round(summed.scanRate * 10) / 10;
    tiles.push(summed);
  }

  for (const ent of rates) {
    if (!grouped.has(ent.name)) {
      tiles.push(ent);
    }
  }

  return tiles
    .filter((e) => e.scanRate > 0 || e.entries > 0)
    .sort((a, b) => b.scanRate - a.scanRate || b.entries - a.entries);
}

/** Success/error/total scan rates summed across every event, rounded to 0.1/min. */
export function computeScanRateTotals(summary: any): ScanRateTotals {
  const events = summary?.events || {};
  let success = 0;
  let error = 0;
  for (const eventCode of Object.keys(events)) {
    const ev = events[eventCode] || {};
    success += Number(ev.successScanRate || 0);
    error += Number(ev.errorScanRate || 0);
  }
  const totalSuccessRate = Math.round(success * 10) / 10;
  const totalErrorRate = Math.round(error * 10) / 10;
  return {
    totalSuccessRate,
    totalErrorRate,
    totalScanRate: totalSuccessRate + totalErrorRate,
  };
}

/**
 * Result codes rolled up across every event, busiest first. Handles both the
 * object form (`{ rate, total }`) and the legacy scalar form of
 * `event.errorByResultCode[code]`. `percentOfEntries` is relative to the venue's
 * total entries.
 */
export function computeErrorAggregates(summary: any): ErrorAggregate[] {
  const events = summary?.events || {};
  const errorMap: Record<
    string,
    { rate: number; total: number; events: string[]; eventDetails: ErrorEventDetail[] }
  > = {};

  for (const eventCode of Object.keys(events)) {
    const ev = events[eventCode] || {};
    const codes = ev.errorByResultCode || {};
    for (const code of Object.keys(codes)) {
      const entry = codes[code];
      const rate =
        typeof entry === 'object' && entry !== null ? Number(entry.rate || 0) : Number(entry || 0);
      const errTotal = typeof entry === 'object' && entry !== null ? Number(entry.total || 0) : 0;
      if (!errorMap[code]) errorMap[code] = { rate: 0, total: 0, events: [], eventDetails: [] };
      errorMap[code].rate += rate;
      errorMap[code].total += errTotal;
      if (errTotal > 0 || rate > 0) {
        errorMap[code].events.push(eventCode);
        errorMap[code].eventDetails.push({
          eventCode,
          name: ev.name || '',
          rate: Math.round(rate * 10) / 10,
          total: errTotal,
        });
      }
    }
  }

  const totalEntries = Number(summary?.entries || 0);

  return Object.keys(errorMap)
    .map((code) => {
      const errTotal = errorMap[code].total;
      return {
        code,
        rate: Math.round(errorMap[code].rate * 10) / 10,
        total: errTotal,
        percentOfEntries: totalEntries > 0 ? (errTotal / totalEntries) * 100 : 0,
        events: errorMap[code].events,
        eventDetails: errorMap[code].eventDetails.sort((a, b) => b.rate - a.rate || b.total - a.total),
      };
    })
    .sort((a, b) => b.rate - a.rate || b.total - a.total);
}

/** Turns a raw result code like `WRONG_EVENT` into a readable `Wrong Event`. */
export function formatErrorCode(code: string): string {
  if (!code) return '';
  return code
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
